import React, { useEffect, useState, useRef } from "react";
import { Card, Row, Col, Button } from "react-bootstrap";
import { IActionHandler, OrgInfo, MessageDisplayType, AdminDashboardUser } from "@preveil-api";
import { Account, useAppDispatch, GlobalErrorMessages, Message, MessageHandlerDisplayType, MessageAnchors, AdminSuccessMessages, useAppSelector } from "src/common";
import { DefaultTemplate, PageHeader, Icon } from "src/components";
import { AdminRename, DashboardCharts, DashboardSettings, DashboardUsers, Welcome, PrintModal } from ".";
import { uiActions, accountActions } from "src/store";
import { RootState } from "src/store/configureStore";

type AllProps = {
  current_account: Account;
  org_info: OrgInfo
}

function DashboardComponent(props: AllProps) {
  const { current_account, org_info } = props;
  const dispatch = useAppDispatch();
  const licenses = useAppSelector((state: RootState) => state.admin.licenses);
  const dashboard_usage = useAppSelector((state: RootState) => state.admin.dashboard_usage);
  const security_settings = useAppSelector((state: RootState) => state.admin.security_settings);
  const [new_dashboard, setNewDashboard] = useState<boolean>(false);
  const [users_list, setUsersList] = useState<AdminDashboardUser[]>([]);
  const [show_modal, setShowModal] = useState<boolean>(false);
  const has_been_dismissed = useRef<boolean>(false);

  useEffect(() => {
    if (dashboard_usage && dashboard_usage.used === 0 && !has_been_dismissed.current) {
      setNewDashboard(true);
    }
  }, [dashboard_usage]);

  //  Description: Handle actions from children components
  const DashboardRequests = {
    // Description: Pass an optional success Message Refresh account model and the organization
    handleRefresh: (success_message?: Message) => {
      (!!success_message && success_message instanceof Message) && dispatch(uiActions.handleSetMessage(success_message));
      dispatch(accountActions.getLocalAccounts(current_account.user_id));
    },
    handleChangeDomainLock: () => {
      dispatch(uiActions.handleSetMessage(new Message(AdminSuccessMessages.success_changed_domain_lock, MessageHandlerDisplayType.toastr)));
    },
    handlePageErrorMessage: (params: { message: string; stack?: any; display_type?: MessageDisplayType; }) => {
      const display_type = !!params.display_type ? params.display_type : MessageHandlerDisplayType.logger;
      dispatch(uiActions.handleRequestErrors(new Message(params.message, display_type), params.stack));
    },
    handleClose: () => {
      setNewDashboard(false);
      has_been_dismissed.current = true;
    }
  };

  // Description: Handle all actions from Children forms
  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((DashboardRequests as any)[callback] instanceof Function) {
      (DashboardRequests as any)[callback](action_obj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
      DashboardRequests.handlePageErrorMessage({ message, stack: action_obj });
    }
  }

  return <DefaultTemplate className="admin-component dashboard">
    <PageHeader>
      <h1>Usage Report</h1>
    </PageHeader>
    <Card className="card-section position-relative">
      {!!licenses && !!dashboard_usage && !!security_settings && !!users_list && users_list.length > 0 && (
        <Button size="sm" onClick={() => setShowModal(true)} className="print-button"
          data-tooltip-id="pv-tooltip" data-tooltip-content="Print Usage Report" data-tooltip-place="left"
        >
          <Icon className="ficon-printer" />Print
        </Button>
      )}
      <Row className="dashboard-top">
        <Col lg={6}>
          <div className="org-name">
            {/* Name Edit */}
            <AdminRename
              current_account={current_account}
              org_info={org_info}
              handleAction={handlePageActions} />
          </div>
          {
            new_dashboard ?
              <Welcome current_account={current_account} handleAction={handlePageActions} /> :
              <DashboardCharts
                current_account={current_account}
                org_info={org_info}
                handleAction={handlePageActions} />
          }
        </Col>
        <Col lg={6} className="dashboard-settings-col">
          <DashboardSettings
            current_account={current_account}
            org_id={org_info.org_id}
            handleAction={handlePageActions} />
        </Col>
      </Row>
      <Row className="dashboard-bottom">
        <Col>
          <DashboardUsers current_account={current_account} org_info={org_info} setPropsUsersList={setUsersList} />
        </Col>
      </Row>
    </Card>
    <PrintModal
      org_info={org_info}
      show_modal={show_modal}
      setShowModal={setShowModal}
      users_list={users_list}
      licenses={licenses}
      dashboard_usage={dashboard_usage}
      security_settings={security_settings}
    />
  </DefaultTemplate>;
}

export default React.memo(DashboardComponent);
